import { useEffect, useRef, type ReactNode } from 'react'

/**
 * Blocking dialog. Escape and a click on the scrim both close it; focus moves
 * in on open and the page underneath stops scrolling.
 */
export function Modal({
  title,
  onClose,
  children,
  width = '520px',
}: {
  title: string
  onClose: () => void
  children: ReactNode
  width?: string
}) {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    panelRef.current?.focus()
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previous
    }
  }, [onClose])

  return (
    <div className="fixed inset-0 z-100 grid place-items-center overflow-y-auto bg-surface-dark/50 p-4 sm:p-6">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="w-full rounded-[24px] bg-canvas p-6 outline-none sm:p-8"
        style={{ maxWidth: width }}
      >
        <h2 className="t-title-lg text-ink">{title}</h2>
        {children}
      </div>
    </div>
  )
}
